import React, { FormEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { zynity_api } from '../../../config/external-routes';
import { Header } from '../../header/Header';
import { InfoTooltip } from './InfoTooltip';
import './KnowledgeGapsPage.css';

type AccessState = {
  enabled: boolean;
  reason?: string | null;
  email?: string | null;
};

type StatusValue = 'new' | 'reviewing' | 'resolved' | 'ignored';

type GapSummary = {
  id: number;
  domain_key: string;
  prompt_key: string;
  interaction_mode?: string | null;
  question: string;
  knowledge_coverage_score?: number | null;
  retrieval_component_score?: number | null;
  model_component_score?: number | null;
  status: StatusValue;
  owner_user_id?: number | null;
  created_at?: string | null;
};

type ChunkEvidence = {
  source_title?: string;
  section_title?: string;
  score?: number;
  text?: string;
};

type GapDetail = GapSummary & {
  user_id?: number | null;
  answer_excerpt?: string | null;
  retrieved_chunk_summary?: ChunkEvidence[];
  resolution_notes?: string | null;
  source_key_added?: string | null;
  updated_at?: string | null;
};

type Pagination = {
  page: number;
  per_page: number;
  total: number;
  last_page: number;
};

const statusOptions: Array<{ value: StatusValue | ''; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'new', label: 'New' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'ignored', label: 'Ignored' },
];

const statusUpdateOptions: Array<{ value: StatusValue; label: string }> = [
  { value: 'new', label: 'New' },
  { value: 'reviewing', label: 'Reviewing' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'ignored', label: 'Ignored' },
];

const formatDate = (value?: string | null) => {
  if (!value) return 'n/a';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const scoreClass = (score?: number | null) => {
  if (score === null || score === undefined) return 'kg-score kg-score--unknown';
  if (score >= 65) return 'kg-score kg-score--good';
  if (score >= 40) return 'kg-score kg-score--warn';
  return 'kg-score kg-score--bad';
};

const scoreText = (score?: number | null) => (score === null || score === undefined ? 'n/a' : String(score));

const TOOLTIP_TEXT = {
  combinedScore: 'Overall 0-100 knowledge coverage score for this case. It is the average of the Retrieval Score and Model Score. Cases below the threshold (65 by default) are automatically flagged as a gap.',
  retrievalScore: 'How closely the best-matching Zynity knowledge chunk found by search actually matches this question, 0-100. A high score means relevant content was found - it does not by itself guarantee the answer used it well.',
  modelScore: "Zyni's own self-check on whether its answer was actually grounded in the retrieved knowledge for this specific question, 0-100. This catches cases where matching content was found but did not really support the answer given.",
  domainMode: 'Which part of Zynity (domain) and which chat mode (chat, guide, or explore) the question was asked in.',
  status: 'New = not yet reviewed. Reviewing = someone is investigating. Resolved = the RAG docs were updated to close the gap. Ignored = not worth fixing (off-topic, duplicate, or not a real gap).',
  scoreColumn: 'Combined coverage score, 0-100. Red < 40, amber 40-64, green ≥ 65 (the default gap threshold).',
};

export const KnowledgeGapsPage: React.FC = () => {
  const [access, setAccess] = useState<AccessState | null>(null);
  const [accessLoading, setAccessLoading] = useState(true);

  const [statusFilter, setStatusFilter] = useState<StatusValue | ''>('new');
  const [domainFilter, setDomainFilter] = useState('');
  const [items, setItems] = useState<GapSummary[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loadingList, setLoadingList] = useState(false);

  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<GapDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const [statusDraft, setStatusDraft] = useState<StatusValue>('new');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [sourceKeyAdded, setSourceKeyAdded] = useState('');
  const [claimOwner, setClaimOwner] = useState(false);
  const [saving, setSaving] = useState(false);

  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    setAccessLoading(true);
    axios
      .get(zynity_api.engagement_knowledge_gaps_access)
      .then((response) => {
        if (!mounted) return;
        setAccess(response.data?.access || { enabled: false, reason: 'invalid_response' });
      })
      .catch(() => {
        if (!mounted) return;
        setAccess({ enabled: false, reason: 'access_check_failed' });
      })
      .finally(() => {
        if (mounted) setAccessLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);

  const fetchList = async (page = 1) => {
    setLoadingList(true);
    setError('');

    try {
      const params: Record<string, string | number> = { page, per_page: 20 };
      if (statusFilter) params.status = statusFilter;
      if (domainFilter.trim()) params.domain_key = domainFilter.trim();

      const response = await axios.get(zynity_api.engagement_knowledge_gaps, { params });
      setItems(response.data?.items || []);
      setPagination(response.data?.pagination || null);
    } catch (err: any) {
      setItems([]);
      setPagination(null);
      setError(err?.response?.data?.error?.message || 'Unable to load knowledge gaps.');
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    if (access?.enabled) {
      fetchList(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access?.enabled]);

  const handleFilterSubmit = (event: FormEvent) => {
    event.preventDefault();
    fetchList(1);
  };

  const selectGap = async (id: number) => {
    setSelectedId(id);
    setDetail(null);
    setLoadingDetail(true);
    setNotice('');
    setError('');

    try {
      const response = await axios.get(`${zynity_api.engagement_knowledge_gaps}/${id}`);
      const gap: GapDetail = response.data?.knowledge_gap;
      setDetail(gap);
      setStatusDraft(gap.status);
      setResolutionNotes(gap.resolution_notes || '');
      setSourceKeyAdded(gap.source_key_added || '');
      setClaimOwner(false);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Unable to load this knowledge gap.');
    } finally {
      setLoadingDetail(false);
    }
  };

  const closeDetail = () => {
    setSelectedId(null);
    setDetail(null);
  };

  const saveStatus = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedId) return;

    setSaving(true);
    setError('');
    setNotice('');

    try {
      const response = await axios.post(`${zynity_api.engagement_knowledge_gaps}/${selectedId}/status`, {
        status: statusDraft,
        resolution_notes: resolutionNotes,
        source_key_added: sourceKeyAdded,
        claim_owner: claimOwner,
      });
      const gap: GapDetail = response.data?.knowledge_gap;
      setDetail(gap);
      setClaimOwner(false);
      setNotice('Saved.');
      fetchList(pagination?.page || 1);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || 'Unable to save this knowledge gap.');
    } finally {
      setSaving(false);
    }
  };

  if (accessLoading) {
    return (
      <>
        <Header />
        <main className="kg-page">
          <section className="kg-card kg-card--compact">Checking Knowledge Gaps access...</section>
        </main>
      </>
    );
  }

  if (!access?.enabled) {
    return (
      <>
        <Header />
        <main className="kg-page">
          <section className="kg-card kg-card--compact">
            <p className="kg-eyebrow">Zyni Operations</p>
            <h1>Access denied</h1>
            <p>This internal page is available only to approved Zynity support users.</p>
            <p className="kg-muted">Reason: {access?.reason || 'not_allowed'}</p>
          </section>
        </main>
      </>
    );
  }

  return (
    <>
    <Header />
    <main className="kg-page">
      <section className="kg-hero">
        <div>
          <p className="kg-eyebrow">Zyni Operations</p>
          <h1>Knowledge Gaps</h1>
          <p>Review questions Zyni could not answer confidently, see the evidence, and track fixes to the RAG source docs.</p>
        </div>
        <div className="kg-access-pill">Access: {access.email || 'approved'}</div>
      </section>

      <section className="kg-card">
        <form className="kg-form" onSubmit={handleFilterSubmit}>
          <div className="kg-form-grid">
            <div className="kg-field">
              <span className="kg-label-row">
                <label htmlFor="kg-status">Status</label>
                <InfoTooltip text={TOOLTIP_TEXT.status} />
              </span>
              <select id="kg-status" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as StatusValue | '')}>
                {statusOptions.map((option) => (
                  <option key={option.value || 'all'} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>

            <div className="kg-field">
              <label htmlFor="kg-domain">Domain</label>
              <input id="kg-domain" value={domainFilter} onChange={(event) => setDomainFilter(event.target.value)} placeholder="profile_v1" />
            </div>

            <div className="kg-field kg-field--action">
              <button className="kg-secondary" type="submit" disabled={loadingList}>
                {loadingList ? 'Loading...' : 'Apply Filters'}
              </button>
            </div>
          </div>
        </form>
      </section>

      {error && <div className="kg-alert kg-alert--error">{error}</div>}
      {notice && <div className="kg-alert kg-alert--success">{notice}</div>}

      <section className="kg-card kg-list-card">
        {items.length === 0 && !loadingList ? (
          <p className="kg-muted">No knowledge gaps found for this filter.</p>
        ) : (
          <div className="kg-table-wrap">
            <table className="kg-table">
              <thead>
                <tr>
                  <th>Question</th>
                  <th><span className="kg-label-row">Score<InfoTooltip text={TOOLTIP_TEXT.scoreColumn} /></span></th>
                  <th>Domain</th>
                  <th>Status</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr
                    key={item.id}
                    className={`kg-row ${selectedId === item.id ? 'is-selected' : ''}`}
                    onClick={() => selectGap(item.id)}
                  >
                    <td>{item.question}</td>
                    <td><span className={scoreClass(item.knowledge_coverage_score)}>{scoreText(item.knowledge_coverage_score)}</span></td>
                    <td>{item.domain_key}</td>
                    <td>{item.status}</td>
                    <td>{formatDate(item.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pagination && pagination.last_page > 1 && (
          <div className="kg-pagination">
            <button type="button" disabled={pagination.page <= 1 || loadingList} onClick={() => fetchList(pagination.page - 1)}>Previous</button>
            <span>Page {pagination.page} of {pagination.last_page} &middot; {pagination.total} total</span>
            <button type="button" disabled={pagination.page >= pagination.last_page || loadingList} onClick={() => fetchList(pagination.page + 1)}>Next</button>
          </div>
        )}
      </section>

      {loadingDetail && <div className="kg-alert kg-alert--success">Loading evidence...</div>}

      {detail && (
        <section className="kg-detail kg-card">
          <div className="kg-detail-header">
            <button className="kg-back" type="button" onClick={closeDetail}>Close</button>
            <div className="kg-detail-title">
              <p className="kg-eyebrow">Case #{detail.id}</p>
              <h2>{detail.question}</h2>
            </div>
          </div>

          <div className="kg-summary-grid">
            <article className="kg-summary-card">
              <span className="kg-label-row">Combined Score<InfoTooltip text={TOOLTIP_TEXT.combinedScore} /></span>
              <strong className={scoreClass(detail.knowledge_coverage_score)}>{scoreText(detail.knowledge_coverage_score)}</strong>
            </article>
            <article className="kg-summary-card">
              <span className="kg-label-row">Retrieval Score<InfoTooltip text={TOOLTIP_TEXT.retrievalScore} /></span>
              <strong>{scoreText(detail.retrieval_component_score)}</strong>
            </article>
            <article className="kg-summary-card">
              <span className="kg-label-row">Model Score<InfoTooltip text={TOOLTIP_TEXT.modelScore} /></span>
              <strong>{scoreText(detail.model_component_score)}</strong>
            </article>
            <article className="kg-summary-card">
              <span className="kg-label-row">Domain / Mode<InfoTooltip text={TOOLTIP_TEXT.domainMode} /></span>
              <strong>{detail.domain_key} / {detail.interaction_mode || 'n/a'}</strong>
            </article>
          </div>

          <div className="kg-evidence">
            <h3>Zyni&apos;s Answer</h3>
            <p className="kg-answer">{detail.answer_excerpt || 'n/a'}</p>

            <h3>Retrieved Chunks</h3>
            {(!detail.retrieved_chunk_summary || detail.retrieved_chunk_summary.length === 0) ? (
              <p className="kg-muted">No chunks were retrieved for this question.</p>
            ) : (
              <div className="kg-chunks">
                {detail.retrieved_chunk_summary.map((chunk, index) => (
                  <article className="kg-chunk" key={`${chunk.source_title}-${index}`}>
                    <div className="kg-chunk-top">
                      <strong>{chunk.source_title || 'Unknown source'}</strong>
                      <span>{chunk.section_title}</span>
                      {typeof chunk.score === 'number' && <em>{chunk.score.toFixed(3)}</em>}
                    </div>
                    <p>{chunk.text}</p>
                  </article>
                ))}
              </div>
            )}
          </div>

          <form className="kg-status-form" onSubmit={saveStatus}>
            <h3>Resolve This Gap</h3>
            <div className="kg-form-grid">
              <div className="kg-field">
                <span className="kg-label-row">
                  <label htmlFor="kg-status-draft">Update status</label>
                  <InfoTooltip text={TOOLTIP_TEXT.status} />
                </span>
                <select id="kg-status-draft" value={statusDraft} onChange={(event) => setStatusDraft(event.target.value as StatusValue)}>
                  {statusUpdateOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="kg-field kg-field--wide">
                <label htmlFor="kg-source-key">RAG doc updated</label>
                <input
                  id="kg-source-key"
                  value={sourceKeyAdded}
                  onChange={(event) => setSourceKeyAdded(event.target.value)}
                  placeholder="documentation/engagement_engine/11-rag_sources/member/reporting.md"
                />
              </div>
            </div>

            <div className="kg-field">
              <label htmlFor="kg-resolution-notes">Resolution notes</label>
              <textarea
                id="kg-resolution-notes"
                rows={3}
                value={resolutionNotes}
                onChange={(event) => setResolutionNotes(event.target.value)}
                placeholder="What did you add or change to close this gap?"
              />
            </div>

            <label className="kg-checkbox">
              <input type="checkbox" checked={claimOwner} onChange={(event) => setClaimOwner(event.target.checked)} />
              Assign this case to me
            </label>

            <div className="kg-form-actions">
              <button className="kg-primary" type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
        </section>
      )}
    </main>
    </>
  );
};
